/**
 * Генератор партнёрских ссылок Trip.com для страниц размещения
 * 
 * Автоматически собирает партнёрские ссылки Trip.com (через Travelpayouts) 
 * для всех CTA/чипсов/кнопок на страницах «Проживание».
 */

import { getTripDomainForLanguage } from '../utils/trip';

// Европейские страны для определения валюты
const EUROPE_SET = new Set([
    'albania', 'andorra', 'austria', 'belarus', 'belgium', 'bosnia-herzegovina',
    'bulgaria', 'croatia', 'cyprus', 'czech-republic', 'denmark', 'estonia',
    'finland', 'france', 'germany', 'greece', 'hungary', 'iceland', 'ireland',
    'italy', 'latvia', 'liechtenstein', 'lithuania', 'luxembourg', 'malta',
    'moldova', 'monaco', 'montenegro', 'netherlands', 'north-macedonia', 'norway',
    'poland', 'portugal', 'romania', 'san-marino', 'serbia', 'slovakia',
    'slovenia', 'spain', 'sweden', 'switzerland', 'ukraine', 'vatican'
]);

/**
 * Резолвер валют на основе страны
 * @param {string} countrySlug - слаг страны
 * @param {string} stayCurrencyOverride - переопределение валюты из CMS
 * @returns {string} код валюты
 */
export function resolveCurrency(countrySlug, stayCurrencyOverride) {
    // Если есть переопределение валюты из CMS, используем его
    if (stayCurrencyOverride) {
        return stayCurrencyOverride;
    }

    // Специальные случаи для Великобритании и Ирландии
    if (countrySlug === 'uk' || countrySlug === 'ireland') {
        return 'GBP';
    }

    // Европейские страны используют EUR
    if (EUROPE_SET.has(countrySlug)) {
        return 'EUR';
    }

    // По умолчанию USD
    return 'USD';
}

/**
 * Маппинг языков сайта в локали Trip.com
 * @param {string} siteLang - язык сайта (ru, en)
 * @returns {string} локаль для Trip.com
 */
export function mapSiteLangToLocale(siteLang) {
    const localeMap = {
        'ru': 'ru-RU',
        'en': 'en-US'
    };

    return localeMap[siteLang] || 'en-US';
}

/**
 * Генерация дат по умолчанию
 * @param {number} checkInDays - количество дней до заезда (по умолчанию 7)
 * @param {number} checkOutDays - количество дней до выезда (по умолчанию 10)
 * @returns {Object} объект с датами в формате YYYY-MM-DD
 */
export function generateDefaultDates(checkInDays = 7, checkOutDays = 10) {
    const today = new Date();
    const checkIn = new Date(today);
    checkIn.setDate(today.getDate() + checkInDays);

    const checkOut = new Date(today);
    checkOut.setDate(today.getDate() + checkOutDays);

    return {
        checkIn: checkIn.toISOString().split('T')[0],
        checkOut: checkOut.toISOString().split('T')[0]
    };
}

/**
 * Основная функция генерации ссылки Trip.com
 * @param {Object} params - параметры для генерации ссылки
 * @param {string} params.provider - провайдер (всегда 'trip')
 * @param {number} params.countryId - ID страны из country.stay.tripCountryId
 * @param {string} params.currency - валюта из резолвера
 * @param {number} params.cityId - ID города из city.stay.tripCityId
 * @param {string} [params.cityName] - название города из city.stay.tripCityName
 * @param {string} params.locale - локаль из маппинга по siteLang
 * @param {string} params.checkIn - дата заезда в формате YYYY-MM-DD
 * @param {string} params.checkOut - дата выезда в формате YYYY-MM-DD
 * @param {number} [params.adults=2] - количество взрослых
 * @param {string} params.mode - режим ссылки: 'base' | 'keyword' | 'poi' | 'distance' | 'poi+distance'
 * @param {string} [params.keyword] - ключевое слово для mode='keyword'
 * @param {string} [params.poiKey] - ключ в city.stay.poiFilters
 * @param {number} [params.lat] - широта для distance
 * @param {number} [params.lon] - долгота для distance
 * @param {string} params.subId - идентификатор для utm_content
 * @param {Object} [params.poiFilters] - карта фильтров POI
 * @param {Object} [params.mainPoint] - основная точка города {lat, lon}
 * @param {string} [params.language] - код языка для определения домена ('ru', 'en', и т.д.). Если не указан, извлекается из locale
 * @returns {string} финальная партнёрская ссылка
 */
export function buildStayLink({
    _provider = 'trip',
    countryId,
    currency,
    cityId,
    cityName,
    locale,
    checkIn,
    checkOut,
    adults = 2,
    mode = 'base',
    keyword,
    poiKey,
    lat,
    lon,
    subId,
    poiFilters = {},
    mainPoint,
    affiliateProvider,
    language = null // Код языка для определения домена ('ru', 'en', и т.д.)
}) {
    // Определяем домен в зависимости от языка
    // Если язык не передан, извлекаем из locale (например, 'ru-RU' -> 'ru')
    let langCode = language;
    if (!langCode && locale) {
        langCode = locale.split('-')[0].toLowerCase();
    }
    const domain = getTripDomainForLanguage(langCode || 'en');

    // Базовый URL с правильным доменом
    const baseUrl = `https://${domain}/hotels/list`;

    // Создаем объект параметров - URLSearchParams сам закодирует их правильно
    const params = new URLSearchParams({
        city: String(cityId),
        cityName: cityName || '',
        countryId: String(countryId),
        adult: String(adults),
        children: '0',
        crn: '1',
        locale: locale,
        curr: currency,
        barCurr: currency
    });

    // Даты — только если их передали явно: страницы /stay кэшируются на 7 дней, и вшитые при
    // рендере «сегодня + 7» к концу недели становились датами заезда «сегодня» или прошлыми
    // (обход 01.10.2026: 08–11.10 в ссылках Катара). Без дат Trip.com подставляет ближайшие сам.
    if (checkIn && checkOut) {
        params.set('checkin', checkIn);
        params.set('checkout', checkOut);
    }

    // Добавляем параметры в зависимости от режима
    switch (mode) {
        case 'keyword':
            if (keyword) {
                params.set('keyword', keyword); // НЕ кодируем - URLSearchParams сделает это
            }
            break;

        case 'poi':
            if (poiKey && poiFilters) {
                let filterValue = null;
                if (Array.isArray(poiFilters)) {
                    const filter = poiFilters.find(f => f._key === poiKey || f.key === poiKey) || poiFilters[0];
                    filterValue = filter?.value;
                } else if (typeof poiFilters === 'object') {
                    filterValue = poiFilters[poiKey];
                }

                if (filterValue) {
                    // Декодируем, если значение уже закодировано в CMS
                    const decodedValue = filterValue.includes('%') ? decodeURIComponent(filterValue) : filterValue;
                    params.set('listFilters', decodedValue); // НЕ кодируем - URLSearchParams сделает это
                }
            }
            break;

        case 'distance':
            const distanceLat = lat || mainPoint?.lat;
            const distanceLon = lon || mainPoint?.lon;
            if (distanceLat && distanceLon) {
                params.set('sort', 'distance');
                params.set('lat', String(distanceLat));
                params.set('lon', String(distanceLon));
            }
            break;

        case 'poi+distance':
            // Комбинированный режим
            if (poiKey && poiFilters) {
                let filterValue = null;
                if (Array.isArray(poiFilters)) {
                    const filter = poiFilters.find(f => f._key === poiKey || f.key === poiKey) || poiFilters[0];
                    filterValue = filter?.value;
                } else if (typeof poiFilters === 'object') {
                    filterValue = poiFilters[poiKey];
                }

                if (filterValue) {
                    const decodedValue = filterValue.includes('%') ? decodeURIComponent(filterValue) : filterValue;
                    params.set('listFilters', decodedValue);
                }
            }
            const combinedLat = lat || mainPoint?.lat;
            const combinedLon = lon || mainPoint?.lon;
            if (combinedLat && combinedLon) {
                params.set('sort', 'distance');
                params.set('lat', String(combinedLat));
                params.set('lon', String(combinedLon));
            }
            break;

        case 'base':
        default:
            // Базовый режим: если есть POI фильтры, добавляем первый доступный
            if (Array.isArray(poiFilters) && poiFilters.length > 0) {
                const firstFilter = poiFilters[0];

                if (firstFilter && firstFilter.value) {
                    // Декодируем, если значение уже закодировано в CMS
                    const decodedValue = firstFilter.value.includes('%') ? decodeURIComponent(firstFilter.value) : firstFilter.value;
                    params.set('listFilters', decodedValue);
                }
            }
            break;
    }

    // Собираем Trip.com URL (URLSearchParams автоматически кодирует параметры ОДИН раз)
    const tripUrl = `${baseUrl}?${params.toString()}`;

    // Если есть affiliate provider, добавляем партнерские параметры
    if (affiliateProvider) {
        // Создаем параметры для партнерской программы
        const affParams = new URLSearchParams();

        // Добавляем defaultParams из Sanity (SID, allianceid, utm_campaign)
        if (affiliateProvider.defaultParams && Array.isArray(affiliateProvider.defaultParams)) {
            affiliateProvider.defaultParams.forEach(param => {
                if (param.key && param.value) {
                    affParams.set(param.key, param.value);
                }
            });
        }

        // Добавляем subId с правильным именем параметра (trip_sub1 для Trip.com)
        const subIdParam = affiliateProvider.subIdParam || 'trip_sub1';
        if (subId) {
            affParams.set(subIdParam, subId);
        }

        // Формируем финальную партнёрскую ссылку (добавляем параметры к Trip.com URL)
        const finalUrl = `${tripUrl}&${affParams.toString()}`;

        return finalUrl;
    }

    // Если нет affiliate provider, возвращаем прямую ссылку Trip.com
    return tripUrl;
}

/**
 * Удобная функция для генерации ссылки с автоматическим резолвингом параметров
 * @param {Object} params - параметры
 * @param {string} params.countrySlug - слаг страны
 * @param {string} params.citySlug - слаг города
 * @param {string} params.siteLang - язык сайта
 * @param {Object} params.countryData - данные страны из CMS
 * @param {Object} params.cityData - данные города из CMS
 * @param {Object} params.affiliateProvider - данные провайдера из CMS
 * @param {Object} params.linkParams - параметры ссылки (mode, keyword, etc.)
 * @returns {string} финальная партнёрская ссылка
 */
export function buildStayLinkFromData({
    countrySlug,
    citySlug,
    siteLang,
    countryData,
    cityData,
    affiliateProvider,
    linkParams = {}
}) {
    // Резолвим валюту
    const currency = resolveCurrency(
        countrySlug,
        countryData?.stay?.stayCurrencyOverride
    );

    // Маппим язык в локаль
    const locale = mapSiteLangToLocale(siteLang);

    // Без дат: см. buildStayLink — вшитые даты протухали в недельном кэше страницы

    // Извлекаем данные из CMS
    const countryId = countryData?.stay?.tripCountryId || countryData?.stay?.stay?.tripCountryId;
    const cityId = cityData?.stay?.tripCityId;
    const cityName = cityData?.stay?.tripCityName || cityData?.name;
    const poiFilters = cityData?.stay?.poiFilters || {};
    const mainPoint = cityData?.stay?.mainPoint;

    // Проверяем обязательные параметры и используем фоллбэк
    if (!countryId || !cityId) {
        console.warn('⚠️ Missing required Trip.com IDs:', { countryId, cityId, countrySlug, citySlug });
        // Используем фоллбэк данные для генерации ссылки
        const fallbackCountryId = 257; // Montenegro
        const fallbackCityId = 9808; // Kotor
        const fallbackCityName = cityName || 'Kotor';

        return buildStayLink({
            countryId: fallbackCountryId,
            currency,
            cityId: fallbackCityId,
            cityName: fallbackCityName,
            locale,
            adults: linkParams.adults || 2,
            mode: linkParams.mode || 'base',
            keyword: linkParams.keyword,
            poiKey: linkParams.poiKey,
            lat: linkParams.lat,
            lon: linkParams.lon,
            subId: linkParams.subId,
            poiFilters,
            mainPoint,
            affiliateProvider,
            language: siteLang
        });
    }

    // Если нет провайдера, возвращаем базовую ссылку Trip.com
    if (!affiliateProvider) {
        console.warn('⚠️ No affiliate provider found, using direct Trip.com link');
        return buildStayLink({
            countryId,
            currency,
            cityId,
            cityName,
            locale,
            adults: linkParams.adults || 2,
            mode: linkParams.mode || 'base',
            keyword: linkParams.keyword,
            poiKey: linkParams.poiKey,
            lat: linkParams.lat,
            lon: linkParams.lon,
            subId: linkParams.subId || `${citySlug}-${linkParams.mode || 'all'}`,
            poiFilters,
            mainPoint,
            affiliateProvider,
            language: siteLang
        });
    }

    // Формируем subId на основе параметров
    const subId = linkParams.subId || `${citySlug}-${linkParams.mode || 'all'}`;

    // Генерируем ссылку
    return buildStayLink({
        countryId,
        currency,
        cityId,
        cityName,
        locale,
        adults: linkParams.adults || 2,
        mode: linkParams.mode || 'base',
        keyword: linkParams.keyword,
        poiKey: linkParams.poiKey,
        lat: linkParams.lat,
        lon: linkParams.lon,
        subId,
        poiFilters,
        mainPoint,
        affiliateProvider,
        language: siteLang
    });
}

// trackAccommodationClick удалён 2026-08-11 (аудит §4.5): дублировал
// affiliate_click из AffiliateLink под третьим именем события; mode теперь
// уходит в affiliate_click как position (см. StayLinkButton).


/**
 * Функции для генерации ссылок Trip.com по странам (не городам)
 * Используется для блока "Типы размещения" на странице stay страны
 * 
 * Создаёт ссылки с фильтрами по всей стране с партнёрскими параметрами
 */

/**
 * Создаёт Trip.com URL для поиска по всей стране с фильтрами
 * 
 * @param {Object} params - параметры для генерации ссылки
 * @param {number} params.tripCountryId - ID страны Trip.com (например, 257 для Montenegro)
 * @param {string} params.countryName - название страны (для searchWord, для RU версии используется русское название)
 * @param {string} [params.checkin] - дата заезда YYYY/MM/DD (опционально, убрано чтобы ссылка не "старела")
 * @param {string} [params.checkout] - дата выезда YYYY/MM/DD (опционально, убрано чтобы ссылка не "старела")
 * @param {number} params.adults - количество взрослых (по умолчанию 2)
 * @param {number} params.children - количество детей (по умолчанию 0)
 * @param {string} params.locale - локаль (например, "ru-RU" или "en-US")
 * @param {string} params.curr - валюта (например, "EUR")
 * @param {Object} params.selector - объект фильтра: { mode: 'keyword', keyword: 'hotel' } или { mode: 'tag', tagId: 123, label: 'Hotels' }
 * @param {string} params.subId - идентификатор для отслеживания (например, "montenegro-hotels")
 * @param {Object} params.affiliateProvider - данные провайдера из Sanity (с SID, allianceid и т.д.)
 * @param {string} [params.language] - код языка для определения домена ('ru', 'en', и т.д.). Если не указан, извлекается из locale
 * @returns {string} финальная партнёрская ссылка
 */
export function buildTripCountryUrl({
    tripCountryId,
    countryName,
    checkin = null, // Опционально, убрано чтобы ссылка не "старела"
    checkout = null, // Опционально, убрано чтобы ссылка не "старела"
    adults = 2,
    children = 0,
    locale = 'ru-RU',
    curr = 'EUR',
    selector,
    subId,
    affiliateProvider,
    language = null // Код языка для определения домена ('ru', 'en', и т.д.)
}) {
    // Определяем домен в зависимости от языка
    // Если язык не передан, извлекаем из locale (например, 'ru-RU' -> 'ru')
    let langCode = language;
    if (!langCode && locale) {
        langCode = locale.split('-')[0].toLowerCase();
    }
    const domain = getTripDomainForLanguage(langCode || 'en');

    // Базовый URL для поиска по стране с правильным доменом
    const base = new URL(`https://${domain}/hotels/list`);

    // Основные параметры для поиска по стране
    base.searchParams.set('city', '-1');
    base.searchParams.set('provinceId', '0');
    base.searchParams.set('districtId', '0');
    base.searchParams.set('countryId', String(tripCountryId));

    // Даты убраны, чтобы ссылка не "старела" (опционально можно добавить обратно)
    if (checkin) {
        base.searchParams.set('checkin', checkin);
    }
    if (checkout) {
        base.searchParams.set('checkout', checkout);
    }

    // Валюта (убираем дубликат barCurr, оставляем только curr)
    base.searchParams.set('curr', curr);

    // Гости
    base.searchParams.set('crn', '1'); // количество комнат
    base.searchParams.set('adult', String(adults));
    base.searchParams.set('children', String(children));

    // Тип поиска по стране
    base.searchParams.set('searchType', 'C'); // C = Country
    base.searchParams.set('searchWord', countryName);
    // Заменяем ~ на | в searchValue (URL-encoded: %7E → %7C)
    base.searchParams.set('searchValue', `140|${tripCountryId}**${tripCountryId}*`);
    // searchCoordinate - необязательный параметр, убираем для упрощения
    // base.searchParams.set('searchCoordinate', 'BAIDU_-1_-1_0~GAODE_-1_-1_0~GOOGLE_-1_-1_0~NORMAL_-1_-1_0');

    // Дополнительные параметры (минимально необходимые)
    base.searchParams.set('searchBoxArg', 't');
    // travelPurpose, ctm_ref, domestic - необязательные, убираем для упрощения
    // base.searchParams.set('travelPurpose', '0');
    // base.searchParams.set('ctm_ref', 'ix_sb_dl');
    // base.searchParams.set('domestic', 'false');

    // Локаль
    base.searchParams.set('locale', locale);

    // Применяем селекторы (фильтры по типам размещения)
    if (selector) {
        if (selector.mode === 'keyword') {
            // Фильтр через keyword (например, "hotel", "apartment")
            base.searchParams.set('keyword', selector.keyword);
        } else if (selector.mode === 'tag') {
            // Фильтр через listFilters (TAG)
            // Формат: 75~TAG_<id>*75*<id>*<label>
            const filter = `75~TAG_${selector.tagId}*75*${selector.tagId}*${encodeURIComponent(selector.label)}`;
            base.searchParams.set('listFilters', filter);
        }
    }

    // Базовый URL Trip.com готов
    const tripUrl = base.toString();

    // Добавляем партнёрские параметры
    if (affiliateProvider) {
        const affParams = new URLSearchParams();

        // Добавляем defaultParams из Sanity (SID, allianceid, utm_campaign)
        if (affiliateProvider.defaultParams && Array.isArray(affiliateProvider.defaultParams)) {
            affiliateProvider.defaultParams.forEach(param => {
                if (param.key && param.value) {
                    affParams.set(param.key, param.value);
                }
            });
        }

        // Добавляем subId (trip_sub1)
        const subIdParam = affiliateProvider.subIdParam || 'trip_sub1';
        if (subId) {
            affParams.set(subIdParam, subId);
        }

        // Формируем финальную партнёрскую ссылку
        const finalUrl = `${tripUrl}&${affParams.toString()}`;

        return finalUrl;
    }

    // console.log('⚠️ Affiliate provider не найден, возвращаем прямую ссылку');
    return tripUrl;
}

/**
 * Создаёт Trip.com URL для поиска по всей стране с множественными фильтрами
 * 
 * @param {Object} params - параметры для генерации ссылки
 * @param {number} params.tripCountryId - ID страны Trip.com (например, 257 для Montenegro)
 * @param {string} params.countryName - название страны (для searchWord, для RU версии используется русское название)
 * @param {string} [params.checkin] - дата заезда YYYY/MM/DD (опционально, убрано чтобы ссылка не "старела")
 * @param {string} [params.checkout] - дата выезда YYYY/MM/DD (опционально, убрано чтобы ссылка не "старела")
 * @param {number} params.adults - количество взрослых (по умолчанию 2)
 * @param {number} params.children - количество детей (по умолчанию 0)
 * @param {string} params.locale - локаль (например, "ru-RU" или "en-US")
 * @param {string} params.curr - валюта (например, "EUR")
 * @param {Array} params.selectors - массив объектов фильтров: [{ mode: 'tag', tagId: 123, label: 'Hotels' }, ...]
 * @param {string} params.subId - идентификатор для отслеживания (например, "montenegro-hotels")
 * @param {Object} params.affiliateProvider - данные провайдера из Sanity (с SID, allianceid и т.д.)
 * @param {string} [params.language] - код языка для определения домена ('ru', 'en', и т.д.). Если не указан, извлекается из locale
 * @returns {string} финальная партнёрская ссылка
 */
export function buildTripCountryUrlWithMultipleFilters({
    tripCountryId,
    countryName,
    checkin = null, // Опционально, убрано чтобы ссылка не "старела"
    checkout = null, // Опционально, убрано чтобы ссылка не "старела"
    adults = 2,
    children = 0,
    locale = 'ru-RU',
    curr = 'EUR',
    selectors = [],
    subId,
    affiliateProvider,
    language = null // Код языка для определения домена ('ru', 'en', и т.д.)
}) {
    // Определяем домен в зависимости от языка
    // Если язык не передан, извлекаем из locale (например, 'ru-RU' -> 'ru')
    let langCode = language;
    if (!langCode && locale) {
        langCode = locale.split('-')[0].toLowerCase();
    }
    const domain = getTripDomainForLanguage(langCode || 'en');

    // Базовый URL для поиска по стране с правильным доменом
    const base = new URL(`https://${domain}/hotels/list`);

    // Основные параметры для поиска по стране
    base.searchParams.set('city', '-1');
    base.searchParams.set('provinceId', '0');
    base.searchParams.set('districtId', '0');
    base.searchParams.set('countryId', String(tripCountryId));

    // Даты убраны, чтобы ссылка не "старела" (опционально можно добавить обратно)
    if (checkin) {
        base.searchParams.set('checkin', checkin);
    }
    if (checkout) {
        base.searchParams.set('checkout', checkout);
    }

    // Валюта (убираем дубликат barCurr, оставляем только curr)
    base.searchParams.set('curr', curr);

    // Гости
    base.searchParams.set('crn', '1'); // количество комнат
    base.searchParams.set('adult', String(adults));
    base.searchParams.set('children', String(children));

    // Тип поиска по стране
    base.searchParams.set('searchType', 'C'); // C = Country
    base.searchParams.set('searchWord', countryName);
    // Заменяем ~ на | в searchValue (URL-encoded: %7E → %7C)
    base.searchParams.set('searchValue', `140|${tripCountryId}**${tripCountryId}*`);
    // searchCoordinate - необязательный параметр, убираем для упрощения
    // base.searchParams.set('searchCoordinate', 'BAIDU_-1_-1_0~GAODE_-1_-1_0~GOOGLE_-1_-1_0~NORMAL_-1_-1_0');

    // Дополнительные параметры (минимально необходимые)
    base.searchParams.set('searchBoxArg', 't');
    // travelPurpose, ctm_ref, domestic - необязательные, убираем для упрощения
    // base.searchParams.set('travelPurpose', '0');
    // base.searchParams.set('ctm_ref', 'ix_sb_dl');
    // base.searchParams.set('domestic', 'false');

    // Локаль
    base.searchParams.set('locale', locale);

    // Применяем множественные селекторы (фильтры по типам размещения)
    if (selectors && selectors.length > 0) {
        const filters = [];

        selectors.forEach(selector => {
            if (selector.mode === 'keyword') {
                // Фильтр через keyword (например, "hotel", "apartment")
                base.searchParams.set('keyword', selector.keyword);
            } else if (selector.mode === 'tag') {
                // Фильтр через listFilters (TAG)
                // Формат: 75~TAG_<id>*75*<id>*<label>
                const filter = `75~TAG_${selector.tagId}*75*${selector.tagId}*${encodeURIComponent(selector.label)}`;
                filters.push(filter);
            }
        });

        // Если есть TAG фильтры, объединяем их через запятую
        if (filters.length > 0) {
            base.searchParams.set('listFilters', filters.join(','));
        }
    }

    // Базовый URL Trip.com готов
    const tripUrl = base.toString();

    // Отладочная информация (можно убрать в продакшене)
    // console.log('🌍 СОЗДАНИЕ ССЫЛКИ ПО СТРАНЕ С МНОЖЕСТВЕННЫМИ ФИЛЬТРАМИ:', { countryName, tripCountryId, checkin, checkout, selectorsCount: selectors.length });

    // Добавляем партнёрские параметры
    if (affiliateProvider) {
        const affParams = new URLSearchParams();

        // Добавляем defaultParams из Sanity (SID, allianceid, utm_campaign)
        if (affiliateProvider.defaultParams && Array.isArray(affiliateProvider.defaultParams)) {
            affiliateProvider.defaultParams.forEach(param => {
                if (param.key && param.value) {
                    affParams.set(param.key, param.value);
                }
            });
        }

        // Добавляем subId (trip_sub1)
        const subIdParam = affiliateProvider.subIdParam || 'trip_sub1';
        if (subId) {
            affParams.set(subIdParam, subId);
        }

        // Формируем финальную партнёрскую ссылку
        const finalUrl = `${tripUrl}&${affParams.toString()}`;

        // Отладочная информация (можно убрать в продакшене)
        // console.log('✅ ПАРТНЁРСКАЯ ССЫЛКА ПО СТРАНЕ С МНОЖЕСТВЕННЫМИ ФИЛЬТРАМИ СОЗДАНА!', { finalUrl, subId, affParamsCount: Array.from(affParams.entries()).length });

        return finalUrl;
    }

    // console.log('⚠️ Affiliate provider не найден, возвращаем прямую ссылку');
    return tripUrl;
}

/**
 * Удобная функция для генерации ссылки по стране с автоматическим резолвингом параметров
 * 
 * @param {Object} params - параметры
 * @param {string} params.countrySlug - слаг страны (например, "montenegro")
 * @param {string} params.countryName - название страны (например, "Montenegro" или "Черногория" для RU)
 * @param {string} params.siteLang - язык сайта ("ru" или "en")
 * @param {Object} params.countryData - данные страны из CMS
 * @param {Object} params.affiliateProvider - данные провайдера из CMS
 * @param {Object} params.selector - объект фильтра
 * @param {string} params.subIdSuffix - суффикс для subId (например, "hotels")
 * @returns {string} финальная партнёрская ссылка
 */
export function buildTripCountryLinkFromData({
    countrySlug,
    countryName,
    siteLang,
    countryData,
    affiliateProvider,
    selector,
    subIdSuffix
}) {
    // Получаем tripCountryId из данных страны
    const tripCountryId = countryData?.stay?.tripCountryId;

    if (!tripCountryId) {
        console.error('❌ ОШИБКА: tripCountryId не найден в данных страны!');
        console.error('Данные страны:', countryData);
        return '#'; // Возвращаем заглушку
    }

    // Резолвим валюту
    const currency = resolveCurrency(countrySlug, countryData?.stay?.stayCurrencyOverride);

    // Резолвим локаль
    const locale = mapSiteLangToLocale(siteLang);

    // Даты убраны, чтобы ссылка не "старела"
    // const { checkIn, checkOut } = generateDefaultDates(7, 10);
    // const checkin = checkIn.replace(/-/g, '/');
    // const checkout = checkOut.replace(/-/g, '/');

    // Определяем название страны в зависимости от языка
    // Для русской версии используем русское название, для остальных - английское
    const localizedCountryName = siteLang === 'ru' && countryData?.nameRu
        ? countryData.nameRu
        : (countryName || countryData?.name || countrySlug);

    // Формируем subId
    const subId = `${countrySlug}-${subIdSuffix}`;

    // Создаём ссылку
    return buildTripCountryUrl({
        tripCountryId,
        countryName: localizedCountryName,
        checkin: null, // Убрано чтобы ссылка не "старела"
        checkout: null, // Убрано чтобы ссылка не "старела"
        adults: 2,
        children: 0,
        locale,
        curr: currency,
        selector,
        subId,
        affiliateProvider,
        language: siteLang // Передаём язык для правильного домена
    });
}

/**
 * Удобная функция для генерации ссылки по стране с множественными фильтрами
 * 
 * @param {Object} params - параметры
 * @param {string} params.countrySlug - слаг страны (например, "montenegro")
 * @param {string} params.countryName - название страны (например, "Montenegro" или "Черногория" для RU)
 * @param {string} params.siteLang - язык сайта ("ru" или "en")
 * @param {Object} params.countryData - данные страны из CMS
 * @param {Object} params.affiliateProvider - данные провайдера из CMS
 * @param {Array} params.types - массив типов размещения из Sanity
 * @param {string} params.subIdSuffix - суффикс для subId (например, "hotels")
 * @returns {string} финальная партнёрская ссылка
 */
export function buildTripCountryLinkWithMultipleFilters({
    countrySlug,
    countryName,
    siteLang,
    countryData,
    affiliateProvider,
    types = [],
    subIdSuffix
}) {
    // Отладочная информация (можно убрать в продакшене)
    // console.log('🔧 СОЗДАНИЕ ССЫЛКИ ПО СТРАНЕ С МНОЖЕСТВЕННЫМИ ФИЛЬТРАМИ:', { countrySlug, countryName, siteLang, typesCount: types.length });

    // Получаем tripCountryId из данных страны
    const tripCountryId = countryData?.stay?.tripCountryId;

    if (!tripCountryId) {
        console.error('❌ ОШИБКА: tripCountryId не найден в данных страны!', countryData);
        return '#'; // Возвращаем заглушку
    }

    // Фильтруем активные типы и получаем их селекторы
    const activeTypes = types.filter(t => t.active !== false);
    const selectors = activeTypes.map(type => type.selector).filter(Boolean);

    if (selectors.length === 0) {
        // console.log('⚠️ Нет активных селекторов для создания ссылки');
        return '#';
    }

    // Резолвим валюту
    const currency = resolveCurrency(countrySlug, countryData?.stay?.stayCurrencyOverride);

    // Резолвим локаль
    const locale = mapSiteLangToLocale(siteLang);

    // Даты убраны, чтобы ссылка не "старела"
    // const { checkIn, checkOut } = generateDefaultDates(7, 10);
    // const checkin = checkIn.replace(/-/g, '/');
    // const checkout = checkOut.replace(/-/g, '/');

    // Определяем название страны в зависимости от языка
    // Для русской версии используем русское название, для остальных - английское
    const localizedCountryName = siteLang === 'ru' && countryData?.nameRu
        ? countryData.nameRu
        : (countryName || countryData?.name || countrySlug);

    // Формируем subId
    const subId = `${countrySlug}-${subIdSuffix}`;

    // Отладочная информация (можно убрать в продакшене)
    // console.log('✅ Параметры подготовлены:', { tripCountryId, currency, locale, subId, selectorsCount: selectors.length });

    // Создаём ссылку с множественными фильтрами
    return buildTripCountryUrlWithMultipleFilters({
        tripCountryId,
        countryName: localizedCountryName,
        checkin: null, // Убрано чтобы ссылка не "старела"
        checkout: null, // Убрано чтобы ссылка не "старела"
        adults: 2,
        children: 0,
        locale,
        curr: currency,
        selectors,
        subId,
        affiliateProvider,
        language: siteLang // Передаём язык для правильного домена
    });
}

